import { PRODUCING_CREATIVE_TEMPLATE_PREVIEW_MESSAGE } from "../i18n/keysTranslations";
import {
  extractCreativeJobResultUrls,
  extractResizeResultUrls,
} from "./creativeJobResultUrls";
import {
  ALERTS_ASYNC_STATUS,
  CREATIVE_TEMPLATE_PRODUCE_INTENT,
} from "./constants";
import {
  CREATIVE_JOB_IN_PROGRESS_STATUSES,
  CREATIVE_JOB_STATUS,
  CREATIVE_JOB_TERMINAL_STATUSES,
} from "./creativeJobStatus";
import { getCreativeJobProgress } from "./creativeJobProgressStorage";
import {
  BULK_CAMPAIGNS_JOB_KIND,
  CREATIVE_ALERT_MODAL_TYPE,
  CREATIVE_ALERT_TYPE,
  getCreativeJobAlertConfig,
  isCreativeJobAlertEnabled,
} from "./creativeJobAlertConfig";
import { CREATIVE_JOB_TYPES, errorCodeToText } from "./errors";
import { getCustomReportSourcePath } from "./customReportSource";
import { normalizeFormatIDs } from "../features/creativeTemplates/utils/slides";

export { isCreativeJobAlertEnabled };
export const CREATIVE_JOB_RTDB_SOURCE = "creativeJobRtdb";

export const getCreativeJobAlertMessageKey = ({ jobType, intent } = {}) => {
  if (
    jobType === CREATIVE_JOB_TYPES.produceCreativeTemplate &&
    intent === CREATIVE_TEMPLATE_PRODUCE_INTENT.PREVIEW
  ) {
    return PRODUCING_CREATIVE_TEMPLATE_PREVIEW_MESSAGE;
  }

  return getCreativeJobAlertConfig(jobType).messageKeys.loading;
};

export const mapJobTypeToNotificationType = (jobType) =>
  getCreativeJobAlertConfig(jobType).alertType;

const BULK_CAMPAIGNS_KIND_BY_JOB_TYPE = {
  [CREATIVE_JOB_TYPES.analyzeBulkCampaigns]: BULK_CAMPAIGNS_JOB_KIND.analysis,
  [CREATIVE_JOB_TYPES.generateBulkCampaigns]:
    BULK_CAMPAIGNS_JOB_KIND.generation,
};

const isBulkCampaignsJobType = (jobType) =>
  Boolean(BULK_CAMPAIGNS_KIND_BY_JOB_TYPE[jobType]);

// A finished analysis that found nothing is shown as an error (as before).
const isBulkAnalysisWithoutCampaigns = (job) =>
  job?.jobType === CREATIVE_JOB_TYPES.analyzeBulkCampaigns &&
  job?.status === CREATIVE_JOB_STATUS.SUCCESS &&
  !(job?.response?.data?.campaigns?.length > 0);

const getMediaTypeFromJobType = (jobType) => {
  if (jobType === CREATIVE_JOB_TYPES.generateVideo) {
    return "video";
  }

  return "image";
};

const resolveCreateWithIAMediaType = ({
  payloadSnapshot = {},
  jobType,
} = {}) => {
  if (
    payloadSnapshot.mediaType === "video" ||
    jobType === CREATIVE_JOB_TYPES.generateVideo
  ) {
    return "video";
  }

  if (
    payloadSnapshot.isCarousel === true ||
    payloadSnapshot.mediaType === "carousel"
  ) {
    return "carousel";
  }

  return payloadSnapshot.mediaType || getMediaTypeFromJobType(jobType);
};

export const normalizeCreativeJobs = (snapshotVal) =>
  Object.entries(snapshotVal || {})
    .map(([jobID, job]) => ({ jobID, ...job }))
    .sort((a, b) => (b.creationTime || 0) - (a.creationTime || 0));

export const mapJobStatusToAlertStatus = (status) => {
  if (status === CREATIVE_JOB_STATUS.SUCCESS) {
    return ALERTS_ASYNC_STATUS.SUCCESS;
  }
  if (status === CREATIVE_JOB_STATUS.ERROR) {
    return ALERTS_ASYNC_STATUS.ERROR;
  }
  if (status === CREATIVE_JOB_STATUS.CANCELLED) {
    return ALERTS_ASYNC_STATUS.ERROR;
  }
  if (CREATIVE_JOB_IN_PROGRESS_STATUSES.has(status)) {
    return ALERTS_ASYNC_STATUS.LOADING;
  }

  return ALERTS_ASYNC_STATUS.LOADING;
};

const getJobResultPayload = (job) => {
  const data = job?.response?.data;
  if (data === undefined || data === null) {
    return null;
  }

  return data;
};

const isUsableResultPayload = (payload) => {
  if (!payload) return false;

  if (Array.isArray(payload.results) && payload.results.length > 0) {
    return true;
  }

  if (
    Array.isArray(payload.createdContents) &&
    payload.createdContents.length > 0
  ) {
    return true;
  }

  if (payload.kind === "images") {
    return (
      (Array.isArray(payload.images) && payload.images.length > 0) ||
      (Array.isArray(payload.results) && payload.results.length > 0)
    );
  }

  if (payload.kind === "video") {
    return Array.isArray(payload.videos) && payload.videos.length > 0;
  }

  if (payload.kind === "url") {
    return Boolean(payload.url);
  }

  if (payload.campaign) {
    return true;
  }

  if (payload.kind === "layers") {
    return Boolean(payload.sourceUrl && payload.urlHash);
  }

  if (payload.kind === "template") {
    return Boolean(payload.templateID);
  }

  // Legacy payloads without kind
  return Boolean(
    payload.url ||
      (Array.isArray(payload.images) && payload.images.length > 0) ||
      (Array.isArray(payload.videos) && payload.videos.length > 0)
  );
};

const hasJobResultData = (job) =>
  isUsableResultPayload(getJobResultPayload(job));

export const isVisibleCreativeJob = (job) => {
  if (!job?.jobID) return false;
  if (getJobPayloadSnapshot(job).hideActivity === true) return false;
  if (!isCreativeJobAlertEnabled(job.jobType)) return false;
  // A later success write must not bring back a job the user already cancelled.
  if (job.cancelled === true) return false;
  if (job.status === CREATIVE_JOB_STATUS.CANCELLED) return false;

  if (CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job.status)) return true;
  if (job.status === CREATIVE_JOB_STATUS.ERROR) return true;

  if (job.status === CREATIVE_JOB_STATUS.SUCCESS) {
    if (isBulkCampaignsJobType(job.jobType)) return true;
    if (job.jobType === CREATIVE_JOB_TYPES.produceCreativeTemplate) {
      return Boolean(job.payloadSnapshot?.produceID) || hasJobResultData(job);
    }
    return hasJobResultData(job);
  }

  return false;
};

const mapImagesResultToGeneratedMedia = (
  images = [],
  { carouselID, isCarousel } = {}
) => {
  const newImages = [];
  const newRatings = {};
  const carouselsById = {};
  const carouselOrder = [];
  const fallbackCarouselID =
    carouselID || (isCarousel ? "notification-carousel" : null);

  images.forEach((image) => {
    if (!image?.url) return;
    newRatings[image.url] = image.rating;

    const itemCarouselID = image.carouselID || fallbackCarouselID;
    if (itemCarouselID) {
      if (!carouselsById[itemCarouselID]) {
        carouselsById[itemCarouselID] = [];
        carouselOrder.push(itemCarouselID);
      }
      carouselsById[itemCarouselID].push(image.url);
      return;
    }

    newImages.push(image.url);
  });

  const carouselItems = carouselOrder.map((carouselID) => ({
    type: "carousel",
    carouselID,
    urls: carouselsById[carouselID],
    rating: null,
  }));

  return {
    generatedMedia: [...carouselItems, ...newImages],
    ratings: newRatings,
  };
};

const mapVideosResultToGeneratedMedia = (videos = []) =>
  videos
    .map((video) => {
      if (typeof video === "string") return video;
      if (!video?.url) return null;
      const sourceUrl = video.sourceUrl || video.originalVideoUrl || "";
      if ((!sourceUrl || sourceUrl === video.url) && !video.subtitles) {
        return video.url;
      }
      return {
        url: video.url,
        ...(sourceUrl && sourceUrl !== video.url ? { sourceUrl } : {}),
        ...(video.subtitles ? { subtitles: video.subtitles } : {}),
      };
    })
    .filter(Boolean);

const buildTerminalGenerationData = ({
  baseGenerationData,
  generatedMedia,
  job,
  ratings,
}) => ({
  ...baseGenerationData,
  isGenerating: false,
  ...(job?.response ? { jobResponse: job.response } : {}),
  ...(generatedMedia?.length ? { generatedMedia } : {}),
  ...(ratings ? { ratings } : {}),
});

export const mapJobResultToGenerationData = ({ job, payloadSnapshot = {} }) => {
  if (isBulkCampaignsJobType(job?.jobType)) {
    const result = getJobResultPayload(job);
    const isAnalysis = job.jobType === CREATIVE_JOB_TYPES.analyzeBulkCampaigns;
    const errorCode = isBulkAnalysisWithoutCampaigns(job)
      ? "noCampaignsFound"
      : job.status === CREATIVE_JOB_STATUS.ERROR
        ? job.response?.code || null
        : null;

    return {
      modalType: CREATIVE_ALERT_MODAL_TYPE.bulkCampaigns,
      kind: BULK_CAMPAIGNS_KIND_BY_JOB_TYPE[job.jobType],
      fileName: payloadSnapshot.fileName || result?.fileName || "",
      platform:
        result?.platform ||
        job.response?.platform ||
        payloadSnapshot.platform ||
        null,
      campaignType:
        result?.campaignType || payloadSnapshot.campaignType || null,
      ...(Array.isArray(payloadSnapshot.contentIDs)
        ? { contentIDs: payloadSnapshot.contentIDs }
        : {}),
      ...(isAnalysis
        ? { campaigns: result?.campaigns || [] }
        : {
            campaignCount:
              payloadSnapshot.campaignCount || result?.campaignIDs?.length || 0,
            ...(Array.isArray(payloadSnapshot.campaigns)
              ? { campaigns: payloadSnapshot.campaigns }
              : {}),
          }),
      ...(!isAnalysis && result
        ? {
            campaignIDs: result.campaignIDs || [],
            failedCount: result.failedCount ?? 0,
            clearedReferences: result.clearedReferences || [],
          }
        : {}),
      ...(errorCode ? { errorCode } : {}),
    };
  }

  if (job?.jobType === CREATIVE_JOB_TYPES.generateCustomReport) {
    const result = getJobResultPayload(job);
    const isInProgress = CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status);

    return {
      ...payloadSnapshot,
      modalType: "customReport",
      customPrompt: payloadSnapshot.customPrompt || "",
      sourcePath:
        payloadSnapshot.sourcePath ||
        getCustomReportSourcePath({ campaignID: payloadSnapshot.campaignID }),
      format: result?.format || payloadSnapshot.format,
      fileName: result?.fileName || payloadSnapshot.fileName,
      downloadUrl: result?.downloadUrl || result?.url || null,
      isGenerating: isInProgress,
    };
  }

  if (job?.jobType === CREATIVE_JOB_TYPES.analyzeImageLayers) {
    const result = getJobResultPayload(job);
    const isInProgress = CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status);

    return {
      ...payloadSnapshot,
      modalType: "imageEditLayers",
      sourceUrl: result?.sourceUrl || payloadSnapshot.url,
      urlHash: result?.urlHash || payloadSnapshot.urlHash,
      isGenerating: isInProgress,
    };
  }

  if (job?.jobType === CREATIVE_JOB_TYPES.analyzeCreativeTemplate) {
    const result = getJobResultPayload(job);

    return {
      ...payloadSnapshot,
      templateID: result?.templateID || payloadSnapshot.templateID,
      layerCount: result?.layerCount || payloadSnapshot.layerCount || 0,
      isGenerating: CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status),
    };
  }

  if (job?.jobType === CREATIVE_JOB_TYPES.produceCreativeTemplate) {
    const result = getJobResultPayload(job);
    const summary = result?.summary || null;
    const artboardIDs = normalizeFormatIDs({
      value: result?.artboardIDs || payloadSnapshot.artboardIDs,
    });
    const intent = payloadSnapshot.intent || job?.payload?.intent;

    return {
      ...payloadSnapshot,
      ...(intent ? { intent } : {}),
      templateID: result?.templateID || payloadSnapshot.templateID,
      produceID: result?.produceID || payloadSnapshot.produceID,
      language: result?.language || payloadSnapshot.language,
      artboardIDs,
      artboardCount:
        artboardIDs.length ||
        result?.artboardCount ||
        payloadSnapshot.artboardCount,
      itemCount: payloadSnapshot.itemCount,
      summary,
      retryFromJobID:
        payloadSnapshot.retryFromJobID ||
        (summary?.failed > 0 ? job?.jobID : null),
      isGenerating: CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status),
    };
  }

  const result = getJobResultPayload(job);
  const config = getCreativeJobAlertConfig(job?.jobType);
  const uiModalType =
    job?.jobType === CREATIVE_JOB_TYPES.editImagesBatch
      ? config.modalType
      : payloadSnapshot.modalType || config.modalType;
  const baseGenerationData = {
    ...payloadSnapshot,
    mediaType: resolveCreateWithIAMediaType({
      payloadSnapshot,
      jobType: job?.jobType,
    }),
    modalType: uiModalType,
    isGenerating: CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job?.status),
    ...(job?.response ? { jobResponse: job.response } : {}),
  };

  if (!result || !CREATIVE_JOB_TERMINAL_STATUSES.has(job?.status)) {
    return baseGenerationData;
  }

  if (result.kind === "images") {
    if (Array.isArray(result.results) && result.results.length > 0) {
      const resizedUrls = extractResizeResultUrls({ ok: true, data: result });
      if (resizedUrls.length > 0) {
        return buildTerminalGenerationData({
          baseGenerationData,
          generatedMedia: resizedUrls,
          job,
        });
      }
    }

    if (Array.isArray(result.images) && result.images.length > 0) {
      const { generatedMedia, ratings } = mapImagesResultToGeneratedMedia(
        result.images,
        {
          carouselID: result.carouselID,
          isCarousel: Boolean(payloadSnapshot.isCarousel),
        }
      );

      return buildTerminalGenerationData({
        baseGenerationData,
        generatedMedia,
        job,
        ratings: {
          ...(payloadSnapshot.ratings || {}),
          ...ratings,
        },
      });
    }
  }

  if (Array.isArray(result.images) && result.images.length > 0) {
    const { generatedMedia, ratings } = mapImagesResultToGeneratedMedia(
      result.images,
      {
        carouselID: result.carouselID,
        isCarousel: Boolean(payloadSnapshot.isCarousel),
      }
    );

    return buildTerminalGenerationData({
      baseGenerationData,
      generatedMedia: [
        ...generatedMedia,
        ...(payloadSnapshot.generatedMedia || []),
      ],
      job,
      ratings: {
        ...(payloadSnapshot.ratings || {}),
        ...ratings,
      },
    });
  }

  if (result.kind === "video" || Array.isArray(result.videos)) {
    return buildTerminalGenerationData({
      baseGenerationData,
      generatedMedia: [
        ...mapVideosResultToGeneratedMedia(result.videos || []),
        ...(payloadSnapshot.generatedMedia || []),
      ],
      job,
    });
  }

  if (result.kind === "url" || result.url || result.urls?.[0]) {
    const resultUrl = result.url || result.urls?.[0];

    if (resultUrl) {
      return buildTerminalGenerationData({
        baseGenerationData,
        generatedMedia: [resultUrl, ...(payloadSnapshot.generatedMedia || [])],
        job,
      });
    }
  }

  const fallbackUrls = extractCreativeJobResultUrls({ response: job.response });
  if (fallbackUrls.length > 0) {
    return buildTerminalGenerationData({
      baseGenerationData,
      generatedMedia: fallbackUrls,
      job,
    });
  }

  return {
    ...baseGenerationData,
    isGenerating: false,
  };
};

const getJobPayloadSnapshot = (job) =>
  job?.payloadSnapshot || job?.payload || {};

const resolveJobErrorMessage = (job) => {
  if (job.status !== CREATIVE_JOB_STATUS.ERROR) {
    return undefined;
  }

  const code = job.response?.code;
  if (code) {
    const translated = errorCodeToText(code);
    if (translated && translated !== code) {
      return translated;
    }
  }

  return job.response?.message;
};

export const mapCreativeJobToNotification = (job) => {
  const payloadSnapshot = getJobPayloadSnapshot(job);
  const config = getCreativeJobAlertConfig(job.jobType);
  const notificationType = config.alertType;
  const alertStatus = isBulkAnalysisWithoutCampaigns(job)
    ? ALERTS_ASYNC_STATUS.ERROR
    : mapJobStatusToAlertStatus(job.status);
  const isTerminal = CREATIVE_JOB_TERMINAL_STATUSES.has(job.status);
  const isGenerating = CREATIVE_JOB_IN_PROGRESS_STATUSES.has(job.status);

  const baseNotification = {
    jobID: job.jobID,
    source: CREATIVE_JOB_RTDB_SOURCE,
    type: notificationType,
    status: alertStatus,
    progress: isTerminal ? 100 : getCreativeJobProgress(job.jobID),
    jobType: job.jobType,
    creationTime: job.creationTime || job.updateTime || Date.now(),
    messageKey: getCreativeJobAlertMessageKey({
      jobType: job.jobType,
      intent: payloadSnapshot.intent || job?.payload?.intent,
    }),
    resultsPersistedAt: job.resultsPersistedAt,
    message: resolveJobErrorMessage(job),
  };

  if (notificationType === CREATIVE_ALERT_TYPE.campaign) {
    const campaignID = payloadSnapshot.campaignID;

    return {
      ...baseNotification,
      id: job.jobID,
      campaignID,
      isCreating: false,
      isGenerating,
      platform: payloadSnapshot.platform,
      currentCampaign: payloadSnapshot.currentCampaign ?? payloadSnapshot,
      platformGeneratingData: payloadSnapshot.platformGeneratingData ?? null,
      newPlatforms:
        payloadSnapshot.newPlatforms ?? payloadSnapshot.platforms ?? null,
      buttonAction: payloadSnapshot.buttonAction ?? null,
    };
  }

  return {
    ...baseNotification,
    id: job.jobID,
    generationData: mapJobResultToGenerationData({
      job,
      payloadSnapshot,
    }),
  };
};

export const mapVisibleCreativeJobsToNotifications = (jobs = []) =>
  jobs.filter(isVisibleCreativeJob).map(mapCreativeJobToNotification);

const CREATIVE_NOTIFICATION_TYPES = new Set([
  CREATIVE_ALERT_TYPE.generatingCreative,
  CREATIVE_ALERT_TYPE.campaign,
  CREATIVE_ALERT_TYPE.generatingCustomReport,
  CREATIVE_ALERT_TYPE.analyzingImageLayers,
  CREATIVE_ALERT_TYPE.analyzingCreativeTemplate,
  CREATIVE_ALERT_TYPE.producingCreativeTemplate,
  CREATIVE_ALERT_TYPE.imageAnimation,
  CREATIVE_ALERT_TYPE.bulkCampaigns,
]);

export const isCreativeNotification = (notification) => {
  if (!notification) return false;
  if (notification.source === CREATIVE_JOB_RTDB_SOURCE) return true;

  return (
    CREATIVE_NOTIFICATION_TYPES.has(notification.type) &&
    Boolean(notification.jobID || notification.campaignID)
  );
};

// Carousels have no `url`; they are identified by `carouselID`.
const getGeneratedMediaKey = (item) => {
  if (typeof item === "string") return item;
  if (item?.type === "carousel" && item.carouselID) {
    return `carousel:${item.carouselID}`;
  }
  return item?.url;
};

export const overlayCreativeGenerationData = (existing, incoming) => {
  if (!incoming) return existing ?? null;
  if (!existing) return incoming;

  const incomingMedia = Array.isArray(incoming.generatedMedia)
    ? incoming.generatedMedia
    : [];
  const existingMedia = (existing.generatedMedia || []).filter((item) => {
    if (!item || typeof item === "string") return true;
    if (item.resizingFormat) return false;
    if (typeof item.url === "string" && !/^https?:\/\//i.test(item.url)) {
      return false;
    }
    return true;
  });

  let generatedMedia = incomingMedia;
  if (incomingMedia.length > 0 && existingMedia.length > 0) {
    const incomingKeys = new Set(
      incomingMedia.map(getGeneratedMediaKey).filter(Boolean)
    );
    generatedMedia = [
      ...incomingMedia,
      ...existingMedia.filter((item) => {
        const key = getGeneratedMediaKey(item);
        return !key || !incomingKeys.has(key);
      }),
    ];
  } else if (incomingMedia.length === 0) {
    generatedMedia = existingMedia;
  }

  return {
    ...existing,
    ...incoming,
    generatedMedia,
  };
};
